import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import type { Booking, User, Tool } from "@/lib/types";

type EnrichedBooking = Booking & { tool?: Tool; borrower?: User; lender?: User };

interface RatingDialogProps {
	open: boolean;
	onClose: () => void;
	booking: EnrichedBooking;
	currentUserId: string;
}

export function RatingDialog({
	open,
	onClose,
	booking,
	currentUserId,
}: RatingDialogProps) {
	const [score, setScore] = useState(5);
	const [hoveredScore, setHoveredScore] = useState(0);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const isBorrower = booking.borrowerId === currentUserId;
	const targetUser = isBorrower ? booking.lender : booking.borrower;
	const ratingType = isBorrower ? "lender" : "borrower";

	async function handleSubmit() {
		if (!targetUser) return;
		setSubmitting(true);
		try {
			await api.createRating({
				toUserId: targetUser.id,
				bookingId: booking.id,
				score,
				comment,
				type: ratingType,
			});
			setSubmitted(true);
			setTimeout(() => {
				onClose();
				setSubmitted(false);
				setScore(5);
				setComment("");
			}, 1500);
		} catch (e) {
			console.error("Failed to submit rating:", e);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{submitted ? "Thanks for your review!" : `Rate your ${ratingType}`}
					</DialogTitle>
					<DialogDescription>
						{submitted
							? "Your feedback helps build trust in the community."
							: `How was your experience with ${targetUser?.name}?`}
					</DialogDescription>
				</DialogHeader>

				{submitted ? (
					<div className="text-center py-6">
						<div className="flex justify-center gap-1">
							{Array.from({ length: 5 }).map((_, i) => (
								<Star
									key={i}
									className={`w-6 h-6 ${
										i < score
											? "fill-yellow-400 text-yellow-400"
											: "text-muted"
									}`}
								/>
							))}
						</div>
					</div>
				) : (
					<div className="space-y-4">
						{targetUser && (
							<div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
								<Avatar className="h-10 w-10">
									<AvatarImage src={targetUser.avatar} />
									<AvatarFallback>{targetUser.name[0]}</AvatarFallback>
								</Avatar>
								<div>
									<p className="font-medium text-sm">{targetUser.name}</p>
									<p className="text-xs text-muted-foreground">
										{booking.tool?.name}
									</p>
								</div>
							</div>
						)}

						<div className="space-y-2">
							<p className="text-sm font-medium">Rating</p>
							<div className="flex gap-1">
								{Array.from({ length: 5 }).map((_, i) => (
									<button
										key={i}
										onMouseEnter={() => setHoveredScore(i + 1)}
										onMouseLeave={() => setHoveredScore(0)}
										onClick={() => setScore(i + 1)}
										className="p-1"
									>
										<Star
											className={`w-7 h-7 transition-colors ${
												i < (hoveredScore || score)
													? "fill-yellow-400 text-yellow-400"
													: "text-muted-foreground/30"
											}`}
										/>
									</button>
								))}
							</div>
						</div>

						<div className="space-y-2">
							<p className="text-sm font-medium">Comment</p>
							<Textarea
								placeholder={
									isBorrower
										? "How was the tool? Was the lender responsive?"
										: "Did the borrower return the tool on time and in good shape?"
								}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								rows={3}
							/>
						</div>

						<div className="flex gap-2">
							<Button variant="outline" className="flex-1" onClick={onClose}>
								Cancel
							</Button>
							<Button
								className="flex-1"
								onClick={handleSubmit}
								disabled={submitting || !comment}
							>
								{submitting ? "Submitting..." : "Submit Review"}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
